import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { OfferOutcome } from '@/components/payments/OfferOutcome';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useBudgetAssessment } from '@/hooks/useBudgetAssessment';
import { useTranslation } from '@/hooks/useTranslation';
import {
  acknowledgeBudgetDisclosureOnConnect,
  type BudgetAnswerInput,
  type BudgetAssessmentTier,
  ConnectApiError,
  completeBudgetAssessmentOnConnect,
  saveBudgetAnswerOnConnect,
  startBudgetAssessmentOnConnect,
} from '@/lib/connect-api';
import { questionsOf, stepsForTier } from '@/lib/budget-assessment-questions';
import { isAccountPaymentPath, parsePlanCount, parsePlanStartDate, REDIRECT_PARAM, safeRedirectTarget } from '@/lib/redirect';
import { useDebtData } from '@/contexts/DebtDataContext';
import { type OfferTerms, sameOfferTerms, useOfferGate } from '@/hooks/useOfferGate';
import { usePayerEmail } from '@/hooks/usePayerEmail';
import { discountedAmountMillicents } from '@/lib/discount-offers';
import { usePaymentApi } from '@/lib/payment-api';
import { paymentReturnUrl, ACCOUNT_SCOPE } from '@/lib/paymentReturnUrl';
import { buildPlanSchedule, toApiSchedule } from '@/lib/payment-schedule';
import { isUnpaidDebt } from '@/lib/debtFilters';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { ChatStep } from './_partials/_budgetAssessmentPartials/ChatStep';
import { CompleteStep } from './_partials/_budgetAssessmentPartials/CompleteStep';
import { DisclosureStep } from './_partials/_budgetAssessmentPartials/DisclosureStep';
import { BudgetPlanStep, type IncomingPlan } from './_partials/_budgetAssessmentPartials/BudgetPlanStep';
import { PlanAuthoriseSheet } from './_partials/_singlePaymentPartials/PlanAuthoriseSheet';
import { budgetLinesFor } from './_partials/_budgetAssessmentPartials/CompleteStep';

/** A dead end (the assessment cannot proceed) or a retryable fault. */
type Failure = 'blocked' | 'network' | null;

const TIER_DEPTH: Record<BudgetAssessmentTier, number> = { short: 1, full: 2 };

export default function BudgetAssessment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accountId, authStage, getAccessToken } = useAuth();
  const { config } = useConfiguration();
  const { state, assessment, setAssessment, isLoading, hasError, refetch } = useBudgetAssessment();
  const { debts, activeOffers, isLoading: debtsLoading } = useDebtData();
  const { createPaymentSession } = usePaymentApi();
  const { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail } = usePayerEmail();
  const [isBusy, setIsBusy] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const [justCompleted, setJustCompleted] = useState(false);
  /**
   * The last plan submitted for a verdict, and whether it came back accepted. Written on every
   * submission and never cleared, which is what two other things depend on:
   *
   *  - the plan step re-seeds its slider from `terms.count`, so "Edit plan" shows the length the
   *    customer settled on rather than the one the URL arrived with;
   *  - an unchanged resubmission is skipped, so Edit-and-straight-back-out records one accepted
   *    offer rather than one per press.
   *
   * A deeper tier surfacing at submit navigates to this same route with `replace`, so nothing
   * remounts — which is why the count has to come from the last *submitted* plan, not the last
   * accepted one.
   */
  const [lastSubmission, setLastSubmission] = useState<{
    terms: OfferTerms & { currency: string };
    accepted: boolean;
  } | null>(null);
  // Card plans stop on the review screen before checkout; "Edit plan" clears this alone, so the
  // submission above survives it.
  const [showReview, setShowReview] = useState(false);
  // Failures the customer can act on from the plan screen, as distinct from `failure`, which
  // replaces the whole page. Losing the plan they just configured to a failed email write or a
  // declined checkout would cost them the assessment they filled in to get here.
  const [planError, setPlanError] = useState<string | null>(null);
  // Set when an assessment was replaced by a deeper one, so the disclosure gate the customer
  // meets straight afterwards can say why rather than looking like lost progress.
  const [askedForDeeper, setAskedForDeeper] = useState(false);

  // An offer that crossed a threshold names the depth it needs and where to hand the customer
  // back. Both are only ever set by the payment flow; a hand-typed or stale link degrades to
  // the standalone assessment it was before.
  const rawTier = searchParams.get('tier');
  const tierParam: BudgetAssessmentTier | undefined = rawTier === 'full' || rawTier === 'short' ? rawTier : undefined;
  const exitTo = safeRedirectTarget(searchParams.get(REDIRECT_PARAM)) ?? '/dashboard';
  // Declared here rather than beside its other callers: the blocked card returns early, above
  // them, and a `const` referenced before its initialiser is a ReferenceError, not a hoist.
  // `replace`, or Back from the restored plan lands straight back on the assessment.
  const exit = () => navigate(exitTo, { replace: true });
  // The engine evaluated the configured thresholds against this specific offer, so when it names
  // a tier that answer wins. `assessment?.tier` is only evidence in the standalone refresh case,
  // where nothing named one.
  const requiredTier: BudgetAssessmentTier = tierParam ?? assessment?.tier ?? 'short';

  // The plan the customer was configuring, read back off the return address the offer gate built.
  // `useDebtData` supplies the balance, which the URL deliberately does not carry: a client that
  // could name its own balance could understate what it owes.
  const returnTo = safeRedirectTarget(searchParams.get(REDIRECT_PARAM));
  const incomingPlan: IncomingPlan | null = useMemo(() => {
    if (!returnTo) return null;
    const [basePath, query] = returnTo.split('?');
    const params = new URLSearchParams(query ?? '');
    const frequency = params.get('frequency');
    if (frequency !== 'weekly' && frequency !== 'biweekly' && frequency !== 'monthly') return null;

    const accountScope = isAccountPaymentPath(basePath);
    const caseId = accountScope ? null : basePath.replace('/payment/', '');
    const payable = debts.filter(isUnpaidDebt);
    const scoped = accountScope ? payable : payable.filter((d) => d.id === caseId);
    if (scoped.length === 0) return null;

    // Per debt, the figure the plan tabs charge: a live `payment_plan` discount replaces the
    // balance. The offer has to be decided on the money the customer will actually be asked for,
    // and the tabs the schedule is built for price it this way, so the raw balance would both
    // submit the wrong instalment and hand back a plan the customer never chose.
    const totalAmount = scoped.reduce((sum, d) => {
      const off = activeOffers[d.id];
      const dMc = off ? discountedAmountMillicents(off, 'payment_plan', Math.round(d.currentBalance * 100_000)) : null;
      const active = off && dMc != null && off.status !== 'expired' && off.status !== 'superseded';
      return sum + (active ? (dMc as number) / 100_000 : d.currentBalance);
    }, 0);

    // Both for the review screen: the pre-discount balance it strikes through, and the creditor
    // it names. An account plan can span creditors, so name the single one or a generic plural —
    // the same rule `AccountPayment` applies to its own review.
    const originalTotalAmount = scoped.reduce((sum, d) => sum + d.currentBalance, 0);
    const creditors = Array.from(new Set(scoped.map((d) => d.originalCreditor)));

    return {
      frequency,
      count: parsePlanCount(params.get('count')) ?? 6,
      startDate: parsePlanStartDate(params.get('start')) ?? new Date(),
      totalAmount,
      originalTotalAmount,
      currency: scoped[0]?.currency ?? 'EUR',
      clientName: creditors.length === 1 ? creditors[0] : t('paymentPlan.review.yourCreditors'),
      caseId,
    } as IncomingPlan & { caseId: string | null };
  }, [returnTo, debts, activeOffers, t]);

  const planCaseId = (incomingPlan as (IncomingPlan & { caseId: string | null }) | null)?.caseId ?? null;
  const offerGate = useOfferGate({ caseId: planCaseId, basePath: returnTo?.split('?')[0] ?? '/dashboard' });

  /**
   * Which failure the customer is shown turns on whether the server answered at all.
   *
   * No status means the request never completed, which is the only case where telling
   * someone to check their connection is honest. Any HTTP status — a 404 for a key outside
   * the frozen set, a 409, or a 500 — is the server refusing, so it takes the branch that
   * offers a route to a human rather than a retry that will keep failing against a cause
   * the customer cannot affect.
   */
  const run = async (action: () => Promise<void>) => {
    setIsBusy(true);
    try {
      await action();
      setFailure(null);
    } catch (error) {
      const status = error instanceof ConnectApiError ? error.status : undefined;
      setFailure(status === undefined ? 'network' : 'blocked');
    } finally {
      setIsBusy(false);
    }
  };

  const startAndAcknowledge = () =>
    run(async () => {
      const draft = await startBudgetAssessmentOnConnect(requiredTier, authStage, getAccessToken);
      setAssessment(await acknowledgeBudgetDisclosureOnConnect(draft.id, authStage, getAccessToken));
      setJustCompleted(false);
    });

  /**
   * Redone at `requiredTier`, which can be shallower than the assessment being replaced: a tier
   * an offer named is the engine's verdict on that offer and outranks the record on file. The
   * deeper record is then lost, and a later offer needing `full` asks for it again.
   */
  const startFresh = () =>
    run(async () => {
      setAssessment(await startBudgetAssessmentOnConnect(requiredTier, authStage, getAccessToken));
      setJustCompleted(false);
      // Nothing remounts here, so a verdict reached against the budget being replaced would
      // otherwise stand and skip the resubmission. Downgraded rather than dropped: the length
      // the customer settled on still seeds the slider on the way back.
      setLastSubmission((prev) => (prev ? { ...prev, accepted: false } : null));
    });

  /**
   * Writes a step's answers as one unit of work. The server takes a line at a time, so a
   * group can still half-land — what this guarantees is that a failure is *reported*: the
   * first rejection stops the rest and is the failure the page shows. Looping `run` per
   * line would instead let a later success clear an earlier line's error, leaving the
   * customer on a group that silently dropped one of their figures.
   *
   * Recovery is the page's retry, which refetches and reopens the group with whatever did
   * land already prefilled.
   */
  const saveAnswers = (entries: readonly (readonly [string, BudgetAnswerInput])[]) =>
    run(async () => {
      if (!assessment) return;
      let latest = assessment;
      for (const [questionKey, answer] of entries) {
        latest = await saveBudgetAnswerOnConnect(assessment.id, questionKey, answer, authStage, getAccessToken);
      }
      setAssessment(latest);
    });

  /**
   * A draft already in flight keeps the tier it was started at — `actions/start` is idempotent
   * on the customer and hands back the existing draft unchanged — so a `short` draft finished
   * under a `full` requirement would be refused by the offer that sent them here. Chaining
   * straight into the deeper assessment is the only thing that breaks that loop; the completed
   * shallow row stays on the record, and the offer decision reads the newest valid one.
   */
  const complete = () =>
    run(async () => {
      if (!assessment) return;
      const completed = await completeBudgetAssessmentOnConnect(assessment.id, authStage, getAccessToken);
      if (TIER_DEPTH[completed.tier] < TIER_DEPTH[requiredTier]) {
        setAssessment(await startBudgetAssessmentOnConnect(requiredTier, authStage, getAccessToken));
        setJustCompleted(false);
        setAskedForDeeper(true);
        return;
      }
      setAssessment(completed);
      setJustCompleted(true);
      // The tier param has now been satisfied, so it stops being evidence. Left in the address
      // bar it outlives `justCompleted`, which is component state: a reload clears the flag and
      // not the URL, and the pair would then read as "an offer refused the assessment on file"
      // and start another one over the top of the answers just given.
      if (tierParam) {
        const next = new URLSearchParams(searchParams);
        next.delete('tier');
        setSearchParams(next, { replace: true });
      }
    });

  /**
   * Builds the plan and hands off to checkout. Throws, so both callers take `run`'s error branch.
   *
   * Everything comes off `terms` rather than live state: an outcome screen can sit open while a
   * discount expires and moves `incomingPlan`, and the schedule has to divide the figure the
   * verdict was taken on.
   */
  const openCheckout = async (terms: OfferTerms & { currency: string }, method: PaymentMethod) => {
    const { frequency, count, startDate, totalAmount, currency } = terms;
    // The same builder both plan tabs use, so the same plan is charged identically whether or
    // not an assessment happened on the way — remainder on the last instalment, and month-end
    // start dates clamped rather than rolled into the following month.
    const schedule = toApiSchedule(buildPlanSchedule(totalAmount, count, startDate, frequency));

    const scope = planCaseId ?? ACCOUNT_SCOPE;
    const response = await createPaymentSession({
      ...(planCaseId ? { case_id: planCaseId } : { account_id: accountId ?? undefined }),
      currency,
      payment_method: method,
      payment_type: 'payment_plan',
      description: `Payment plan: ${count} ${frequency} payments`,
      success_url: paymentReturnUrl({ flow: 'plan', scope }),
      cancel_url: `${window.location.origin}${returnTo ?? '/dashboard'}`,
      payment_plan_schedule: schedule,
      payment_plan_frequency: frequency,
    });

    if (response.checkout_url) {
      window.location.href = response.checkout_url;
      return;
    }
    navigate(`/dashboard?plan_success=${scope}${response.session_id ? `&session_id=${response.session_id}` : ''}`);
  };

  /**
   * Submit the chosen plan for a verdict and, if it is accepted, take the customer on — to the
   * review screen for a card plan, straight to checkout for every other method.
   *
   * The offer has to be decided before a plan is built even though the customer has just proved
   * they can afford it: affordability is one axis, and the length thresholds that decide whether
   * a human reviews the offer are withheld from the portal, so acceptance is never ours to assume.
   */
  const continueWithPlan = (count: number, method: PaymentMethod) =>
    run(async () => {
      if (!incomingPlan) return;
      const { frequency, startDate, totalAmount, currency } = incomingPlan;
      const terms = { frequency, count, startDate, totalAmount, currency };
      setPlanError(null);
      // Written before the verdict, not at checkout: a referred offer never reaches checkout, and
      // Ops is then asked to review a plan whose only contact detail the customer just typed.
      try {
        await persistPayerEmail();
      } catch {
        setPlanError(t('paymentPlan.emailRequired'));
        return;
      }

      // A verdict already taken on these exact numbers stands, so returning here from "Edit plan"
      // and continuing again records one accepted offer rather than one per press.
      const decidedAlready = lastSubmission?.accepted === true && sameOfferTerms(lastSubmission.terms, terms);

      if (!decidedAlready) {
        setLastSubmission({ terms, accepted: false });
        const decided = await offerGate.submit({ frequency, count, startDate, totalAmount });
        if (decided.go === 'assessment') {
          // A deeper requirement surfaced at submit: on to that assessment, rather than the slider
          // this offer has just been refused on.
          navigate(decided.url, { replace: true });
          return;
        }
        // Refused or referred: the gate now holds the verdict, or an error, and the render below
        // shows it. This page owns the completion of the plan, so it owns the refusal too.
        if (decided.go === 'stop') return;
        setLastSubmission({ terms, accepted: true });
      }

      // A card plan reads the review screen before it authorises anything; nothing else has one.
      if (method === 'card') {
        setShowReview(true);
        return;
      }

      try {
        await openCheckout(terms, method);
      } catch {
        setPlanError(t('payment.processingFailed'));
      }
    });

  /**
   * Authorise the reviewed plan. Never through `run`: that answers a throw by replacing the whole
   * page with the dead-end card, so a declined checkout would cost the customer the assessment
   * they filled in to get here.
   */
  const authoriseReviewedPlan = async () => {
    if (!lastSubmission || !incomingPlan) return;
    const { terms } = lastSubmission;

    // The terms are frozen while this screen is open, and a discount expiring refreshes offers
    // without flipping `debtsLoading` — so nothing else would notice the plan no longer matches
    // what is owed, and the schedule would be built on pricing that has lapsed.
    //
    // Deliberately bidirectional, and not the looser `>` a balance check would use: both sides
    // are the same discounted plan figure out of the same reduce, so any inequality means the
    // plan on screen is no longer the plan current state implies — under-covering if the balance
    // grew, over-covering (and owing a refund) if it shrank.
    if (terms.totalAmount !== incomingPlan.totalAmount) {
      setPlanError(t('paymentPlan.review.balanceChanged'));
      setShowReview(false);
      return;
    }

    setIsBusy(true);
    setPlanError(null);
    try {
      await openCheckout(terms, 'card');
    } catch {
      setPlanError(t('payment.processingFailed'));
    } finally {
      setIsBusy(false);
    }
  };

  // The read resolves in an effect, so branching on `state` first would bounce a
  // mid-flow reload to the dashboard before the answer arrives.
  if (isLoading) {
    return (
      <Layout showFooter={false}>
        <Loader fullScreen />
      </Layout>
    );
  }

  // A tier this portal has no wording for is a dead end however it arrived, and saying so
  // up front beats a truncated set followed by a refused completion.
  const steps = assessment ? stepsForTier(assessment.tier) : null;
  const unaskableTier = assessment != null && assessment.status === 'draft' && steps == null;

  // `not_required` with a tier param is a contradiction, not a success: an offer has just asked
  // for an assessment this account is told it does not need. Bouncing would flicker the customer
  // back to a plan that refuses them again, so it takes the dead-end card.
  //
  // `useBudgetAssessment` reports a failed read as `not_required` + `hasError`, so this has to
  // exclude it — otherwise a flaky GET reads as a contradiction and takes the dead-end card
  // instead of the retry below.
  const contradicted = !hasError && state === 'not_required' && tierParam != null;

  // A failed read takes the retryable branch rather than a redirect — `hasError` covers any
  // read fault and `refetch` is the honest affordance. Without this a single flaky GET dropped
  // the customer on the dashboard, silently discarding the plan they had configured.
  if (failure || unaskableTier || hasError || contradicted) {
    const blocked = unaskableTier || contradicted || failure === 'blocked';
    const supportUrl = config.ui.companyInfo?.supportUrl;
    return (
      <Layout showFooter={false}>
        <div className="w-full max-w-sm mx-auto px-4 py-10 space-y-4">
          <Alert variant={blocked ? 'destructive' : 'warning'}>
            <TriangleAlert className="size-4" />
            <AlertTitle>
              {t(blocked ? 'budgetAssessment.errors.unavailableTitle' : 'budgetAssessment.errors.networkTitle')}
            </AlertTitle>
            <AlertDescription>
              {t(blocked ? 'budgetAssessment.errors.unavailableBody' : 'budgetAssessment.errors.networkBody')}
            </AlertDescription>
          </Alert>
          <Button
            className="w-full"
            onClick={() => {
              setFailure(null);
              refetch();
            }}
          >
            {t(blocked ? 'budgetAssessment.errors.startAgain' : 'budgetAssessment.errors.retry')}
          </Button>
          {blocked && supportUrl && (
            <Button variant="outline" className="w-full" onClick={() => window.open(supportUrl, '_blank')}>
              {t('budgetAssessment.errors.contactUs')}
            </Button>
          )}
          {/* Unconditional, and not gated on `supportUrl` being configured. Start Again re-reads
              the same state and reproduces whatever blocked the customer, so without this an
              operator with no support URL leaves them on a screen with no way off it. */}
          <Button variant="ghost" className="w-full" onClick={exit}>
            {t(exitTo === '/dashboard' ? 'budgetAssessment.complete.done' : 'budgetAssessment.complete.continueToPlan')}
          </Button>
        </div>
      </Layout>
    );
  }

  // The flow is switched off for this account and nothing asked for it — send them back where
  // they came from, which is the dashboard unless a payment page handed us a return address.
  if (state === 'not_required') {
    return <Navigate to={exitTo} replace />;
  }


  if (assessment && steps && assessment.status === 'draft' && assessment.disclosure_acknowledged_at) {
    return (
      <Layout showFooter={false}>
        <ChatStep
          assessment={assessment}
          steps={steps}
          onSaveAnswers={saveAnswers}
          onComplete={complete}
          onExit={exit}
          isBusy={isBusy}
        />
      </Layout>
    );
  }

  /**
   * A valid assessment plus a tier param means an offer refused the one on file — too shallow,
   * or held in a currency this offer cannot be decided in, which the portal cannot see because
   * `/current` is currency-blind. Either way "you're all set" is the wrong screen: the customer
   * would press Done, be refused again, and loop. Fall through to the disclosure gate, which
   * starts a fresh assessment at the required depth.
   */
  /**
   * `justCompleted` cannot suppress the too-shallow case, because that is where it happens.
   * Lengthening the plan on the plan step can cross a threshold the assessment just finished
   * does not clear, and the deeper requirement arrives as a navigate to this same route — so
   * the component never remounts and every flag it holds is still set. Comparing the depth on
   * file against the depth now demanded is the only signal that survives that.
   */
  const tooShallowForOffer =
    assessment?.status === 'completed' && TIER_DEPTH[assessment.tier] < TIER_DEPTH[requiredTier];

  const reusableButRefused = tooShallowForOffer || (state === 'reusable' && tierParam != null && !justCompleted);

  const expired = state === 'refresh_required' && !justCompleted;

  const terminalScreen = assessment?.status === 'completed' && !reusableButRefused && !expired;

  // A return address means a plan is inbound, and `incomingPlan` cannot be built until the
  // balances land. Without this wait the customer meets "you're all set" and has it replaced a
  // moment later by the plan screen.
  if (terminalScreen && returnTo != null && debtsLoading) {
    return (
      <Layout showFooter={false}>
        <Loader fullScreen />
      </Layout>
    );
  }

  // The verdict is shown where it was reached. The plan tab it would otherwise hand back to
  // mounts its own gate, whose outcome starts empty, so the refusal would never be rendered.
  if (terminalScreen && offerGate.outcome && incomingPlan) {
    const held = offerGate.outcome;
    return (
      <Layout showFooter={false}>
        <OfferOutcome
          outcome={held.outcome}
          terms={held.terms}
          currency={incomingPlan.currency}
          frequencyLabel={t(`paymentPlan.${held.terms.frequency}`)}
          onChoosePlan={offerGate.reset}
          // The dashboard, not `exitTo` — which is the plan the offer was just refused on. Sending
          // them there would show a slider that explains nothing and, because the return address
          // carries `offer=assessed`, invite them to resubmit the offer they were refused on. Both
          // plan tabs send Done to the dashboard for the same reason.
          onDone={() => navigate('/dashboard')}
          isBusy={isBusy}
          error={planError ?? offerGate.error}
        />
      </Layout>
    );
  }

  // A plan came in with the customer, so the summary is not the end of the journey — it is where
  // they see the plan against the budget and settle its length. An expired assessment is excluded:
  // it has to be redone before any plan can be decided against it.
  if (assessment && terminalScreen && incomingPlan) {
    return (
      <Layout showFooter={false}>
        <BudgetPlanStep
          assessment={assessment}
          // The length the customer last submitted, not the one the URL arrived with, so "Edit
          // plan" and a deeper-tier detour both come back to the plan they had settled on.
          plan={{ ...incomingPlan, count: lastSubmission?.terms.count ?? incomingPlan.count }}
          breakdownFor={(categories) => budgetLinesFor(assessment, categories)}
          onContinue={continueWithPlan}
          onBack={startFresh}
          isBusy={isBusy}
          // The sheet carries the error while it is open, so it is not also shown behind it.
          error={showReview ? null : (planError ?? offerGate.error)}
          needsEmail={needsEmail}
          payerEmail={payerEmail}
          onPayerEmailChange={setPayerEmail}
          emailValid={emailValid}
        />
        {/* The card plan's last word before the card sheet, over the plan step rather than back on
            the plan tab: sending them there would put the configure screen — and its unfloored
            slider — between the budget they just proved and the plan they are authorising. */}
        {lastSubmission && (
          <PlanAuthoriseSheet
            open={showReview}
            onClose={() => {
              setShowReview(false);
              setPlanError(null);
            }}
            totalAmount={lastSubmission.terms.totalAmount}
            currency={lastSubmission.terms.currency}
            clientName={incomingPlan.clientName}
            plan={{ startDate: lastSubmission.terms.startDate, frequency: lastSubmission.terms.frequency, paymentCount: lastSubmission.terms.count }}
            onAuthorise={authoriseReviewedPlan}
            isProcessing={isBusy}
            error={planError}
          />
        )}
      </Layout>
    );
  }

  if (assessment && assessment.status === 'completed' && !reusableButRefused) {
    return (
      <Layout showFooter={false}>
        <CompleteStep
          assessment={assessment}
          expired={expired}
          justCompleted={justCompleted}
          onUpdate={startFresh}
          onDone={exit}
          isBusy={isBusy}
          returnsToPlan={exitTo !== '/dashboard'}
        />
      </Layout>
    );
  }

  // `none`, a draft whose disclosure has not been acknowledged, or a valid assessment an offer
  // refused. The counts have to describe what will actually be asked: a draft is resumed at its
  // own depth (`actions/start` is idempotent), anything else starts at the required one.
  const askedTier = assessment?.status === 'draft' ? assessment.tier : requiredTier;
  const askedSteps = stepsForTier(askedTier) ?? [];

  return (
    <Layout showFooter={false}>
      <DisclosureStep
          onContinue={startAndAcknowledge}
          onBack={exit}
          isLoading={isBusy}
          questionCount={questionsOf(askedSteps).length}
          sectionCount={askedSteps.length}
          deeper={reusableButRefused || askedForDeeper}
        />
    </Layout>
  );
}
